import React, { useEffect, useState } from 'react';
import { ADMIN } from '@/lib/adminApi';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Search, Eye, Download, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = ['new', 'reviewing', 'phone_screen', 'interviewed', 'hired', 'rejected', 'not_a_fit'];
const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  reviewing: 'bg-amber-50 text-amber-700 border-amber-200',
  phone_screen: 'bg-violet-50 text-violet-700 border-violet-200',
  interviewed: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  hired: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  not_a_fit: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function AdminApplications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [noteText, setNoteText] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await ADMIN.apps({ status: statusFilter || undefined, q: q || undefined });
      setRows(r.applications || []);
    } catch { toast.error('Failed to load applications'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [statusFilter]);

  const onStatus = async (app, status) => {
    try { await ADMIN.updateAppStatus(app.id, status); toast.success('Status updated'); reload(); }
    catch { toast.error('Update failed'); }
  };

  const openApp = async (app) => {
    try { const full = await ADMIN.app(app.id); setSelected(full); }
    catch { toast.error('Failed to load application'); }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selected) return;
    try {
      await ADMIN.addAppNote(selected.id, noteText);
      const full = await ADMIN.app(selected.id);
      setSelected(full);
      setNoteText('');
      toast.success('Note added');
    } catch { toast.error('Failed'); }
  };

  const downloadResume = async () => {
    if (!selected) return;
    try {
      const r = await ADMIN.appResume(selected.id);
      const link = document.createElement('a');
      link.href = `data:${r.content_type};base64,${r.base64}`;
      link.download = r.filename || 'resume';
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error('No resume on file'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-applications-view">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Applications</h1>
          <p className="text-sm text-slate-500 mt-1">Careers form submissions.</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="apps-search" placeholder="Search name/email/position" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && reload()} className="pl-9 w-full sm:w-72" />
          </div>
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="apps-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        {loading ? <Skeleton className="h-64" /> : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="apps-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Position</th>
                  <th className="px-4 py-3 font-semibold">Exp.</th>
                  <th className="px-4 py-3 font-semibold">Contact</th>
                  <th className="px-4 py-3 font-semibold">Langs</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-500">No applications yet.</td></tr>}
                {rows.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50" data-testid={`app-row-${a.id}`}>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{a.full_name}</td>
                    <td className="px-4 py-3 text-slate-700">{a.position}</td>
                    <td className="px-4 py-3 text-slate-700">{a.years_experience}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="flex flex-col gap-0.5">
                        {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1 hover:text-[#0A2540]"><Phone className="h-3 w-3" />{a.phone}</a>}
                        {a.email && <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-[#0A2540] truncate max-w-[180px]"><Mail className="h-3 w-3" />{a.email}</a>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{(a.languages || []).join(', ')}</td>
                    <td className="px-4 py-3">
                      <Select value={a.status || 'new'} onValueChange={(v) => onStatus(a, v)}>
                        <SelectTrigger className={`h-8 w-36 capitalize text-xs font-semibold border ${STATUS_COLORS[a.status] || ''}`} data-testid={`app-status-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>))}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="ghost" onClick={() => openApp(a)} data-testid={`app-view-${a.id}`}><Eye className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{selected?.full_name} — <span className="font-normal text-slate-500">{selected?.position}</span></DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Phone" value={selected.phone} link={`tel:${selected.phone}`} />
                <Field label="Email" value={selected.email} link={`mailto:${selected.email}`} />
                <Field label="Address" value={`${selected.address}, ${selected.city}, ${selected.state} ${selected.zip_code}`} />
                <Field label="DOB" value={selected.dob} />
                <Field label="Years exp." value={selected.years_experience} />
                <Field label="Currently employed" value={selected.currently_employed ? 'Yes' : 'No'} />
                <Field label="Start date" value={selected.start_date} />
                <Field label="Driver's license / Transport" value={`${selected.has_drivers_license ? 'Yes' : 'No'} / ${selected.has_transportation ? 'Yes' : 'No'}`} />
                <Field label="Heights / Work auth" value={`${selected.comfortable_with_heights ? 'Yes' : 'No'} / ${selected.work_authorization ? 'Yes' : 'No'}`} />
                <Field label="Languages" value={(selected.languages || []).join(', ')} />
                <Field label="Certifications" value={selected.certifications || '—'} />
                <Field label="Source" value={selected.source || '—'} />
              </div>
              {selected.why_join && <Field label="Why join" value={selected.why_join} block />}
              {selected.has_resume && (
                <Button variant="outline" onClick={downloadResume} data-testid="app-download-resume"><Download className="h-4 w-4 mr-2" /> Download resume ({selected.resume_filename})</Button>
              )}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-2">Notes ({selected.notes?.length || 0})</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(selected.notes || []).map((n, i) => (
                    <div key={i} className="rounded-lg bg-slate-50 p-3 text-sm">
                      <div className="text-xs text-slate-500">{n.author} • {n.timestamp ? new Date(n.timestamp).toLocaleString() : ''}</div>
                      <div className="text-slate-700 mt-1">{n.text}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <Textarea rows={2} placeholder="Add a note" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="app-note-input" />
                  <Button onClick={addNote} className="bg-[#0A2540] hover:bg-[#13294a]" data-testid="app-note-add">Add</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, link, block }) {
  return (
    <div className={`rounded-lg bg-slate-50 p-3 ${block ? 'sm:col-span-2' : ''}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5 break-words">
        {link ? <a href={link} className="text-[#0A2540] hover:underline">{value || '—'}</a> : (value || '—')}
      </div>
    </div>
  );
}
